/**
 * @file communityData.js
 * @description Contains community data for kiosk mode including community names, IDs, and URLs.
 * This file serves as a central repository for community information used in kiosk applications.
 */

/**
 * @typedef {Object} CommunityData
 * @property {string} communityId - Unique identifier for the community
 * @property {string} name - Name of the community
 * @property {string} url - URL or endpoint for the community
 * @property {string} region - Geographic region where the community is located
 * @property {string} status - Current status of the community (Active, Inactive, etc.)
 * @property {string} description - Description of the community and its characteristics
 */

/**
 * Array of community data
 * Contains information about communities with their IDs, names, and URLs
 * @type {CommunityData[]}
 */
export const communityData = [
  {
    communityId: "ARCTIC_BAY",
    name: "Arctic Bay",
    url: "/kiosk/arctic-bay",
    region: "Nunavut",
    status: "Active",
    description:
      "Weather stations managed by Ikpiarjuk HTA",
  },
  {
    communityId: "POND_INLET",
    name: "Pond Inlet",
    url: "/kiosk/pond-inlet",
    region: "Nunavut",
    status: "Active",
    description:
      "Weather stations managed by Sikumiut, SmartICE",
  },
  {
    communityId: "IGLOOLIK",
    name: "Igloolik",
    url: "/kiosk/igloolik",
    region: "Nunavut",
    status: "Active",
    description:
      "Weather stations managed by the Hamlet of Igloolik",
  },
  {
    communityId: "SANIRAJAK",
    name: "Sanirajak",
    url: "/kiosk/sanirajak",
    region: "Nunavut",
    status: "Active",
    description:
      "Weather stations managed by Hall Beach HTA",
  },
  {
    communityId: "TEST",
    name: "TESTING",
    url: "/kiosk/test",
    region: "TESTING",
    status: "Active",
    description:
      "Kiosk testing",
  },
];

/**
 * Helper function to get community by ID
 * @param {string} communityId - The community ID to search for
 * @returns {CommunityData|null} The community data or null if not found
 */
export const getCommunityById = (communityId) => {
  return (
    communityData.find((community) => community.communityId === communityId) ||
    null
  );
};

/**
 * Helper function to get all active communities
 * @returns {CommunityData[]} Array of active communities
 */
export const getActiveCommunities = () => {
  return communityData.filter((community) => community.status === "Active");
};

/**
 * Helper function to get communities by region
 * @param {string} region - The region to filter by
 * @returns {CommunityData[]} Array of communities in the specified region
 */
export const getCommunitiesByRegion = (region) => {
  return communityData.filter((community) => community.region === region);
};

/**
 * Helper function to search communities by name
 * @param {string} searchTerm - The search term to match against community names
 * @returns {CommunityData[]} Array of communities matching the search term
 */
export const searchCommunities = (searchTerm) => {
  const term = searchTerm.toLowerCase();
  return communityData.filter(
    (community) =>
      community.name.toLowerCase().includes(term) ||
      community.communityId.toLowerCase().includes(term)
  );
};
